/**
 * CUSTOM.JS - Custom Script File
 * --------------------------------
 * ⚠️ Ensure this script is connected to the storefront.
 * To enable it, uncomment the <script> tag linking to this file in theme.liquid.
 * 
 * Example:
 * <!-- <script type="module" src="{{ 'custom.js' | asset_url }}"></script> --> ← Uncomment this line
 */

// Afterpay on-site messaging DOM/style overrides intentionally disabled.

/**
 * FIX: Prevent quick-buy modals from opening twice on mobile
 * 
 * Issue: Two <quick-buy-modal> elements with the same ID exist in the DOM
 * (one for mobile, one for desktop layout), causing both to open simultaneously
 * when clicked on mobile.
 * 
 * Solution: Track show() calls globally and prevent duplicates happening
 * within 50ms, ensuring only one modal opens per click.
 * 
 * Metal Matching: Products with the 'metal-matching' tag will automatically
 * have their metal/material option matched to the customer's cart preference.
 */
(function preventDoubleModalOpenAndFixVariant() {
  // Cache for product metal matching configuration
  const metalProductsCache = new Map();
  
  // Cache for cart metal preference (short-lived)
  let cartPreferenceCache = null;
  let cartPreferenceCacheTime = 0;
  const CART_CACHE_DURATION = 5000; // 5 seconds
  
  // Track currently opening modal to prevent duplicates
  let currentlyOpenProductHandle = null;
  let isOpening = false;
  let openingPromise = null;
  
  /**
   * Detect metal type from text (handle or variant name)
   * @param {string} text - Text to analyze
   * @returns {string|null} 'gold' or 'silver' or null
   */
  function detectMetalType(text) {
    if (!text) return null;
    const lower = text.toLowerCase();
    
    // Check for silver keywords first (more specific patterns)
    if (lower.includes('sterling-silver') || 
        lower.includes('sterling silver') || 
        lower.includes('silver')) {
      return 'silver';
    }
    
    // Check for gold keywords (avoid matching "golden" as a color descriptor)
    // Match patterns like: "gold", "-gold", "gold-", "vermeil"
    if (lower.match(/\b(gold|vermeil)\b/) || 
        lower.includes('-gold') || 
        lower.includes('gold-')) {
      return 'gold';
    }
    
    return null;
  }
  
  /**
   * Fetch product data and check if metal matching is enabled via tag
   * @param {string} productHandle - Product handle
   * @returns {Promise<Object|null>} Configuration object or null if not eligible
   */
  async function getMetalMatchingConfig(productHandle) {
    // Check cache first
    if (metalProductsCache.has(productHandle)) {
      const cached = metalProductsCache.get(productHandle);
      return cached;
    }
    
    try {
      const product = await fetch(`${window.Shopify.routes.root}products/${productHandle}.js?t=${Date.now()}`).then(r => r.json());
      
      // Check if metal matching is enabled via tag
      const hasMetalMatchingTag = product.tags?.some(tag => 
        tag.toLowerCase() === 'metal-matching' || 
        tag.toLowerCase() === 'enable-metal-matching'
      );
      
      if (!hasMetalMatchingTag) {
        metalProductsCache.set(productHandle, null);
        return null;
      }
      
      // Check if this is a single-SKU product (no variants or only 1 variant)
      const isSingleSku = !product.options || product.options.length === 0 || 
                          (product.variants && product.variants.length === 1);
      
      if (isSingleSku) {
        // For single-SKU products, detect metal from product handle or title
        const metalType = detectMetalType(productHandle) || detectMetalType(product.title);
        
        const config = {
          position: null,
          metals: [],
          isSingleSku: true,
          metalType: metalType
        };
        
        metalProductsCache.set(productHandle, config);
        return config;
      }
      
      // Find the metal option (look for option containing "metal", "material", etc.)
      let metalOptionPosition = null;
      const metalKeywords = ['metal', 'material', 'finish'];
      
      if (product.options) {
        for (let i = 0; i < product.options.length; i++) {
          const option = product.options[i];
          // Options can be either strings or objects with a "name" property
          const optionName = (typeof option === 'string' ? option : option.name || option).toLowerCase();
          
          if (metalKeywords.some(keyword => optionName.includes(keyword))) {
            metalOptionPosition = i + 1; // Shopify uses 1-based indexing
            break;
          }
        }
      }
      
      if (!metalOptionPosition) {
        metalProductsCache.set(productHandle, null);
        return null;
      }
      
      // Extract available metal values from variants
      const metalValues = new Set();
      if (product.variants) {
        product.variants.forEach(variant => {
          const metalValue = variant[`option${metalOptionPosition}`];
          if (metalValue) {
            metalValues.add(metalValue.toLowerCase().replace(/\s+/g, '-'));
          }
        });
      }
      
      const config = {
        position: metalOptionPosition,
        metals: Array.from(metalValues)
      };
      
      metalProductsCache.set(productHandle, config);
      return config;
      
    } catch (error) {
      console.error(`[Metal Matching] Error fetching config for ${productHandle}:`, error);
      metalProductsCache.set(productHandle, null);
      return null;
    }
  }
  
  // Extract clean product handle without query parameters
  function getProductHandle(fullHandle) {
    return fullHandle ? fullHandle.split('?')[0] : null;
  }
  
  /**
   * Fetch cart and determine metal preference from most recent matching item
   * @returns {Promise<string|null>} Normalized metal type or null
   */
  async function getCartMetalPreference() {
    // Check cache first (5 second TTL)
    const now = Date.now();
    if (cartPreferenceCache !== null && (now - cartPreferenceCacheTime) < CART_CACHE_DURATION) {
      return cartPreferenceCache;
    }
    
    try {
      // Cache-bust to ensure fresh cart data
      const cart = await fetch(`${window.Shopify.routes.root}cart.js?t=${Date.now()}`).then(r => r.json());
      
      if (!cart.items || cart.items.length === 0) return null;
      
      // Shopify cart API returns newest items at index [0]
      for (let i = 0; i < cart.items.length; i++) {
        const item = cart.items[i];
        
        // Check if this item's product has metal matching enabled
        const config = await getMetalMatchingConfig(item.handle);
        if (!config) continue;
        
        // Handle single-SKU products: detect metal from the product itself
        if (config.isSingleSku && config.metalType) {
          cartPreferenceCache = config.metalType;
          cartPreferenceCacheTime = Date.now();
          return config.metalType;
        }
        
        // Handle variant-based products: get the metal option value
        if (config.position) {
          const metalValue = item.options_with_values?.[config.position - 1]?.value;
          if (!metalValue) continue;
          
          const metalLower = metalValue.toLowerCase();
          
          // Determine if this is gold or silver based metal type
          // Return a simple keyword that can be used for matching
          if (metalLower.includes('gold') || metalLower.includes('vermeil')) {
            cartPreferenceCache = 'gold';
            cartPreferenceCacheTime = Date.now();
            return 'gold';
          }
          if (metalLower.includes('silver') || metalLower.includes('sterling')) {
            cartPreferenceCache = 'silver';
            cartPreferenceCacheTime = Date.now();
            return 'silver';
          }
        }
      }
      
      cartPreferenceCache = null;
      cartPreferenceCacheTime = Date.now();
      return null;
    } catch (error) {
      console.error('[Metal Matching] Error getting cart metal preference:', error);
      return null;
    }
  }
  
  /**
   * Auto-select the matching metal option in the modal's variant picker
   * @param {HTMLElement} modalElement - The quick-buy modal element
   * @param {string} metalPreference - The preferred metal type ('gold' or 'silver')
   * @param {string} productHandle - Product handle
   */
  async function selectMatchingMetalOption(modalElement, metalPreference, productHandle) {
    if (!metalPreference) return;
    
    // Verify this product supports metal matching
    const config = await getMetalMatchingConfig(productHandle);
    if (!config || config.isSingleSku || !config.position) return;
    
    const variantPicker = modalElement.querySelector('variant-picker');
    if (!variantPicker) return;
    
    // Find all radio inputs for the metal option
    const metalInputs = variantPicker.querySelectorAll(`input[data-option-position="${config.position}"]`);
    if (metalInputs.length === 0) return;
    
    // Find and select the matching metal input
    for (const input of metalInputs) {
      const label = document.querySelector(`label[for="${input.id}"]`);
      const labelText = label ? label.textContent.trim().toLowerCase() : '';
      
      let shouldSelect = false;
      
      // Match based on simple keywords
      if (metalPreference === 'gold' && (labelText.includes('gold') || labelText.includes('vermeil'))) {
        shouldSelect = true;
      }
      if (metalPreference === 'silver' && (labelText.includes('silver') || labelText.includes('sterling'))) {
        shouldSelect = true;
      }
      
      if (shouldSelect && !input.checked) {
        input.checked = true;
        // Dispatch change event to update the variant picker (price, image, variant ID, etc.)
        input.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      } else if (shouldSelect && input.checked) {
        return;
      }
    }
  }
  
  
  document.addEventListener('DOMContentLoaded', () => {
    // Pre-fetch configurations for all quick-buy modals on page load
    // This ensures instant matching when modals are opened
    const preFetchConfigs = async () => {
      const quickBuyModals = document.querySelectorAll('quick-buy-modal[handle]');
      
      const handles = Array.from(quickBuyModals).map(modal => {
        const fullHandle = modal.getAttribute('handle');
        return getProductHandle(fullHandle);
      }).filter(Boolean);
      
      // Remove duplicates
      const uniqueHandles = [...new Set(handles)];
      
      // Fetch configs in parallel for fast initialization
      await Promise.all(
        uniqueHandles.map(handle => getMetalMatchingConfig(handle).catch(() => null))
      );
    };
    
    // Pre-fetch after a short delay to not block initial page load
    setTimeout(preFetchConfigs, 500);
    
    // Intercept all clicks on quick-buy triggers
    document.addEventListener('click', async (event) => {
      const trigger = event.target.closest('[aria-controls]');
      if (!trigger) return;
      
      const modalId = trigger.getAttribute('aria-controls');
      const modalElement = document.getElementById(modalId);
      if (!modalElement || modalElement.tagName !== 'QUICK-BUY-MODAL') return;
      
      const fullHandle = modalElement.getAttribute('handle');
      const productHandle = getProductHandle(fullHandle);
      
      // Strip variant parameter from handle to always open with first variant
      // This prevents pre-selecting specific variants (e.g., from complementary products)
      if (fullHandle && fullHandle.includes('?variant=')) {
        modalElement.setAttribute('handle', productHandle);
      }
      
      // Prevent double-open: if this product's modal is already opening, wait for it
      if (currentlyOpenProductHandle === productHandle && (openingPromise || isOpening)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (openingPromise) await openingPromise;
        return;
      }
      
      // Lock this product to prevent concurrent opens
      isOpening = true;
      currentlyOpenProductHandle = productHandle;
      event.preventDefault();
      event.stopImmediatePropagation();
      
      // Async opening sequence
      openingPromise = (async () => {
        try {
          // Start fetching cart preference immediately (parallel with modal opening)
          const cartPreferencePromise = getCartMetalPreference();
          const configPromise = getMetalMatchingConfig(productHandle);
          
          // Open the modal immediately
          if (typeof modalElement.show === 'function') {
            await modalElement.show();
          }
          
          // Check if this product supports metal matching
          const config = await configPromise;
          
          // For variant-based products, select the matching metal option
          // Cart preference can come from either variant-based OR single-SKU products
          if (config && !config.isSingleSku) {
            const metalPreference = await cartPreferencePromise;
            
            if (metalPreference) {
              // Reduced wait time - just enough for DOM to stabilize (20ms)
              await new Promise(resolve => setTimeout(resolve, 20));
              await selectMatchingMetalOption(modalElement, metalPreference, productHandle);
            }
          }
        } catch (error) {
          console.error('[Metal Matching] Error:', error);
        } finally {
          // Release lock immediately after modal opens
          setTimeout(() => {
            if (currentlyOpenProductHandle === productHandle) {
              isOpening = false;
              currentlyOpenProductHandle = null;
              openingPromise = null;
            }
          }, 100);
        }
      })();
    }, true); // Use capture phase to intercept before theme handlers
    
    // Clean up state when modal closes
    document.addEventListener('dialog:after-hide', (event) => {
      if (event.target.tagName === 'QUICK-BUY-MODAL') {
        const handle = event.target.getAttribute('handle');
        const productHandle = getProductHandle(handle);
        if (currentlyOpenProductHandle === productHandle) {
          currentlyOpenProductHandle = null;
        }
        isOpening = false;
        openingPromise = null;
      }
    });
    
    // Invalidate cart preference cache when cart changes
    document.addEventListener('cart:change', () => {
      cartPreferenceCache = null;
      cartPreferenceCacheTime = 0;
    });
    
    document.addEventListener('cart:refresh', () => {
      cartPreferenceCache = null;
      cartPreferenceCacheTime = 0;
    });
  });
})();

/**
 * ============================================================================
 * FEATURE WITH ICON VARIANT - Dynamic Variant-Based Feature Rendering
 * ============================================================================
 * 
 * This module handles re-rendering of feature_with_icon_variant blocks when
 * the product variant changes. It uses the Shopify Section Rendering API to
 * fetch updated content based on the selected variant.
 */
(function() {
  'use strict';

  /**
   * Fetches and updates variant-based feature blocks for a specific section
   * @param {string} sectionId - The ID of the section to re-render
   * @param {number} variantId - The selected variant ID
   */
  async function updateVariantFeatureBlocks(sectionId, variantId) {
    // Find all variant feature blocks in the current section
    const section = document.querySelector(`#shopify-section-${sectionId}`);
    if (!section) return;

    const variantFeatureBlocks = section.querySelectorAll('[data-variant-feature-block]');
    if (variantFeatureBlocks.length === 0) return;

    try {
      // Fetch the section HTML with the new variant
      const url = `${window.location.pathname}?section_id=${sectionId}&variant=${variantId}`;
      const response = await fetch(url);
      
      if (!response.ok) {
        console.error('[Variant Feature Blocks] Failed to fetch section:', response.statusText);
        return;
      }

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // The fetched HTML is just the section content, not wrapped in shopify-section
      const newVariantBlocks = doc.querySelectorAll('[data-variant-feature-block]');

      // Update each variant feature block by matching block IDs
      variantFeatureBlocks.forEach(oldBlock => {
        const blockId = oldBlock.dataset.blockId;
        if (!blockId) return;
        
        // Find the corresponding block in the new HTML by block ID
        const newBlock = Array.from(newVariantBlocks).find(block => block.dataset.blockId === blockId);
        
        if (newBlock) {
          // Safari-compatible replacement: use parentNode.replaceChild instead of replaceWith
          if (oldBlock.parentNode) {
            oldBlock.parentNode.replaceChild(newBlock, oldBlock);
          }
        } else {
          oldBlock.style.display = 'none';
        }
      });

    } catch (error) {
      console.error('[Variant Feature Blocks] Error updating variant feature blocks:', error);
    }
  }

  /**
   * Initialize variant change listeners for all product forms
   */
  function initVariantFeatureBlocks() {
    // Listen for variant:change events on all product forms
    document.addEventListener('variant:change', async (event) => {
      const form = event.target;
      
      // Get the variant data from the event
      const { variant } = event.detail;
      if (!variant) return;

      // Find the parent Shopify section
      const section = form.closest('.shopify-section');
      if (!section) return;

      // Extract section ID from the shopify-section id attribute
      const sectionId = section.id.replace('shopify-section-', '');
      if (!sectionId) return;

      // Update the variant feature blocks
      await updateVariantFeatureBlocks(sectionId, variant.id);
    });
    
    // Also listen for change events on variant picker inputs as a fallback for Safari
    document.addEventListener('change', async (event) => {
      const target = event.target;
      
      // Check if this is a variant picker input
      if (target && target.hasAttribute && target.hasAttribute('data-option-position')) {
        // Find the form
        const formId = target.getAttribute('form');
        if (!formId) return;
        
        const form = document.getElementById(formId);
        if (!form) return;
        
        // Wait a bit for the variant to update
        setTimeout(async () => {
          // Find the variant ID from the form's hidden input
          const variantInput = form.querySelector('input[name="id"]');
          if (!variantInput || !variantInput.value) return;
          
          const variantId = variantInput.value;
          
          // Find the parent Shopify section
          const section = form.closest('.shopify-section');
          if (!section) return;
          
          const sectionId = section.id.replace('shopify-section-', '');
          if (!sectionId) return;
          
          await updateVariantFeatureBlocks(sectionId, variantId);
        }, 100);
      }
    }, true); // Use capture phase
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initVariantFeatureBlocks);
  } else {
    initVariantFeatureBlocks();
  }
})();

/**
 * Cart cross-sell carousel scroll position management.
 *
 * Behaviour:
 *  - Always starts at position 0 when no prior scroll is stored.
 *  - Remembers the user's scroll position (per carousel id) in sessionStorage
 *    so it survives tab reloads within the same browser session.
 *  - Clears stored positions when any cart item is removed or the cart empties
 *    (covers checkout completion and manual item removal).
 *  - No polling. Cleanup runs on carousel removal to avoid memory leaks.
 *  - Scoped strictly to .cart-drawer__complementary-products carousels so no
 *    other scroll-carousel instances on the page are affected.
 */
(function cartCrossSellCarouselMemory() {
  'use strict';

  const STORAGE_KEY = 'seraphi:cross-sell-scroll';
  const CROSS_SELL_SELECTOR = '.cart-drawer__complementary-products scroll-carousel';
  const DEBOUNCE_MS = 150;

  // Active debounce timer handles keyed by carousel id string (not element refs).
  const debounceTimers = new Map();

  // Sorted product_id strings from the last known cart state.
  // Stays null until the first cart:change or bootstrap fetch resolves.
  let lastProductIds = null;

  // ── sessionStorage helpers ───────────────────────────────────────────────

  function loadPositions() {
    try { return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null') || {}; }
    catch { return {}; }
  }

  function savePosition(carouselId, scrollLeft) {
    const data = loadPositions();
    data[carouselId] = Math.round(scrollLeft);
    try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data)); }
    catch {}
  }

  function clearAllPositions() {
    try { sessionStorage.removeItem(STORAGE_KEY); }
    catch {}
  }

  function getStoredPosition(carouselId) {
    const pos = loadPositions()[carouselId];
    return (Number.isFinite(pos) && pos > 0) ? pos : null;
  }

  // ── Per-carousel init / teardown ─────────────────────────────────────────

  function initCarousel(carousel) {
    // Only handle carousels inside the cross-sell section that have a stable id.
    if (!carousel.id || !carousel.closest('.cart-drawer__complementary-products')) return;
    // Prevent double-initialisation if the same element is seen twice.
    if (carousel._seraphi_csm) return;
    carousel._seraphi_csm = true;

    const carouselId = carousel.id;
    const stored = getStoredPosition(carouselId);

    // Reset scroll immediately (synchronous) to override any lingering browser
    // scroll-restoration state, then apply the desired position after two
    // animation frames — this ensures we run after the browser's own
    // scroll-restoration pass and any layout/paint triggered by connectedCallback.
    carousel.scrollLeft = 0;
    requestAnimationFrame(() => requestAnimationFrame(() => {
      if (!document.contains(carousel)) return;
      carousel.scrollLeft = (stored !== null) ? stored : 0;
    }));

    // Debounced scroll listener — saves position only after the user stops scrolling.
    const onScroll = () => {
      const prev = debounceTimers.get(carouselId);
      if (prev) clearTimeout(prev);
      debounceTimers.set(
        carouselId,
        setTimeout(() => {
          debounceTimers.delete(carouselId);
          savePosition(carouselId, carousel.scrollLeft);
        }, DEBOUNCE_MS)
      );
    };
    carousel.addEventListener('scroll', onScroll, { passive: true });

    // Expose teardown on the element so the removal branch can clean up.
    carousel._seraphi_cleanup = () => {
      carousel.removeEventListener('scroll', onScroll);
      const t = debounceTimers.get(carouselId);
      if (t) { clearTimeout(t); debounceTimers.delete(carouselId); }
      delete carousel._seraphi_csm;
      delete carousel._seraphi_cleanup;
    };
  }

  function teardownCarousel(carousel) {
    if (typeof carousel._seraphi_cleanup === 'function') carousel._seraphi_cleanup();
  }

  // ── DOM observation ──────────────────────────────────────────────────────

  function walkNodes(nodes, fn) {
    nodes.forEach(node => {
      if (node.nodeType !== 1) return;
      if (node.tagName === 'SCROLL-CAROUSEL') fn(node);
      node.querySelectorAll?.('scroll-carousel').forEach(fn);
    });
  }

  const domObserver = new MutationObserver(mutations => {
    for (const { addedNodes, removedNodes } of mutations) {
      walkNodes(addedNodes, initCarousel);
      walkNodes(removedNodes, teardownCarousel);
    }
  });
  domObserver.observe(document.body, { childList: true, subtree: true });

  // The cart drawer is server-rendered: carousels already exist in the DOM
  // before observation starts, so explicitly initialise them now.
  document.querySelectorAll(CROSS_SELL_SELECTOR).forEach(initCarousel);

  // ── Cart-state tracking ──────────────────────────────────────────────────

  function syncCartState(cart) {
    if (!cart || cart.item_count === 0) {
      clearAllPositions();
      lastProductIds = null;
      return;
    }

    const newIds = cart.items.map(i => String(i.product_id)).sort();

    if (lastProductIds !== null) {
      // If any previously seen product has been removed, invalidate stored positions.
      const anyRemoved = lastProductIds.some(id => !newIds.includes(id));
      if (anyRemoved) clearAllPositions();
    }

    lastProductIds = newIds;
  }

  // Respond to live cart changes (item added, quantity changed, item removed).
  document.addEventListener('cart:change', event => syncCartState(event.detail?.cart));

  // Bootstrap the initial cart state via a single lightweight fetch. This
  // handles the case where the user completed checkout (empty cart) and
  // returned to the store in the same session — stale positions are cleared
  // before any carousel is ever shown. Non-critical; silently ignored on error.
  fetch('/cart.js').then(r => r.json()).then(syncCartState).catch(() => {});
})();
